import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface Brevo {
  _id: string;
  apiKey?: string;
}

export function SettingsBrevo() {
  const [brevo, setBrevo] = useState<Brevo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBrevo, setIsCreatingBrevo] = useState(false);
  const [isDeletingBrevo, setIsDeletingBrevo] = useState(false);
  const [isSavingBrevo, setIsSavingBrevo] = useState(false);
  const [showBrevoApiKey, setShowBrevoApiKey] = useState(false);
  const [brevoApiKey, setBrevoApiKey] = useState("");

  useEffect(() => {
    async function fetchBrevo() {
      setIsLoading(true);
      try {
        const brevoResponse = await fetch("/api/brevo");
        if (brevoResponse.ok) {
          const brevoData = await brevoResponse.json();
          if (brevoData.brevo) {
            setBrevo(brevoData.brevo);
            setBrevoApiKey(brevoData.brevo.apiKey || "");
          }
        } else if (brevoResponse.status !== 404) {
          console.error("Failed to fetch Brevo integration");
        }
      } catch (error) {
        console.error("Failed to fetch Brevo integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrevo();
  }, []);

  const handleCreateBrevo = async () => {
    setIsCreatingBrevo(true);
    try {
      const response = await fetch("/api/brevo", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.brevo) {
          setBrevo(data.brevo);
          setBrevoApiKey(data.brevo.apiKey || "");
          toast.success("Brevo integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Brevo integration");
      }
    } catch (error) {
      console.error("Error creating Brevo integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Brevo integration");
    } finally {
      setIsCreatingBrevo(false);
    }
  };

  const handleSaveBrevo = async () => {
    if (!brevo) return;

    setIsSavingBrevo(true);
    try {
      const response = await fetch("/api/brevo", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: brevoApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.brevo) {
          setBrevo(data.brevo);
          toast.success("Brevo settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Brevo settings");
      }
    } catch (error) {
      console.error("Error saving Brevo settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Brevo settings");
    } finally {
      setIsSavingBrevo(false);
    }
  };

  const handleDeleteBrevo = async () => {
    if (!brevo) return;

    if (!confirm("Are you sure you want to delete your Brevo integration?")) {
      return;
    }

    setIsDeletingBrevo(true);
    try {
      const response = await fetch(`/api/brevo/${brevo._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setBrevo(null);
        setBrevoApiKey("");
        toast.success("Brevo integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Brevo integration");
      }
    } catch (error) {
      console.error("Error deleting Brevo integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Brevo integration");
    } finally {
      setIsDeletingBrevo(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Brevo</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Brevo account to manage email subscribers and transactional email
            </p>
            {brevo && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {!brevo ? (
            <Button onClick={handleCreateBrevo} disabled={isCreatingBrevo}>
              {isCreatingBrevo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          ) : (
            <Button onClick={handleDeleteBrevo} disabled={isDeletingBrevo} variant="destructive">
              {isDeletingBrevo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        {brevo && (
          <div className="space-y-2 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="brevoApiKey">API Key</Label>
              <div className="relative">
                <Input
                  id="brevoApiKey"
                  type={showBrevoApiKey ? "text" : "password"}
                  value={brevoApiKey}
                  onChange={(e) => setBrevoApiKey(e.target.value)}
                  placeholder="Enter Brevo API key"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowBrevoApiKey(!showBrevoApiKey)}
                >
                  {showBrevoApiKey ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            <Button onClick={handleSaveBrevo} disabled={isSavingBrevo}>
              {isSavingBrevo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
